import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { MaterialBoolControl } from 'console/shared/components/fields';
import { Box, Paper, Typography } from '@mui/material';
import { TerminalHints } from 'console/shared/components/panels/terminal/hints';
import { Capability } from 'console/device/device-capability';
import { LegacyDevice } from 'console/device/device';
import { requestDeviceUpdate } from 'console/redux/devices/items/actions';
import { TerminalLine } from 'console/shared/components/panels/terminal/line';
import { send_terminal_command } from 'console/shared/components/panels/terminal/redux/actions';
import { useBox, usePartial } from 'shared/hooks/utils';
import { useAction, useAppSelector, usePartialAppSelector, } from 'shared/hooks/redux/hooks';
import { createSelector } from '@reduxjs/toolkit';
const getTerminalItems = createSelector((state, device_id) => device_id, (state) => state.terminal, (device_id, terminal_state) => {
    if (device_id == null) {
        return [];
    }
    const commands = terminal_state[device_id];
    if (!commands) {
        return [];
    }
    return commands;
});
export const TerminalCard = memo(function TerminalCard(props) {
    const { r_device: device, is_staff_CP, commands_CP, onRequestDeviceUpdate_CP, debug_text_messages_CP, } = props;
    const device_id = useMemo(() => device.id, [device.id]);
    const is_staff = is_staff_CP ?? useAppSelector((state) => state.account.is_staff);
    const show_hints = useMemo(() => (device != null &&
        LegacyDevice.has_capability(device.capabilities, Capability.has_classic_autoscan_commands)) ||
        is_staff, [device.capabilities, is_staff]);
    const terminal_items = commands_CP ??
        usePartialAppSelector(getTerminalItems, device_id);
    const boxed_terminal_items = useBox(terminal_items);
    const debug_text_message = useMemo(() => ![undefined, null, ''].includes(device?.debug_text_messages_regexp), [device?.debug_text_messages_regexp]);
    const onSendCommand = usePartial(useAction(send_terminal_command), device_id);
    const onRequestDeviceUpdate = useAction(requestDeviceUpdate);
    const onSetDebugTextMessages = useCallback((checked) => {
        if (onRequestDeviceUpdate_CP) {
            onRequestDeviceUpdate_CP(checked);
        }
        return onRequestDeviceUpdate(device_id, {
            debug_text_messages_regexp: checked ? '.' : '',
        });
    }, [onRequestDeviceUpdate, device_id]);
    const [input, setInput] = useState({});
    const [history_offset, setHistoryOffset] = useState({});
    const terminal_ref = useRef(null);
    const onInputChange = useCallback((ev) => {
        setInput({
            ...input,
            [device_id]: ev.target.value,
        });
    }, [input, device_id]);
    const getHistory = useCallback((offset) => {
        if (offset > 0) {
            return boxed_terminal_items.current[boxed_terminal_items.current.length - offset].request;
        }
        return '';
    }, []);
    const adjustHistoryOffset = useCallback((delta) => {
        let _history_offset = history_offset[device_id] ?? 0;
        const orig_history = getHistory(_history_offset);
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const prev_offset = _history_offset;
            _history_offset = Math.min(Math.max(_history_offset + delta, 0), boxed_terminal_items.current.length);
            if (_history_offset === prev_offset)
                break;
            if (getHistory(_history_offset) !== orig_history)
                break;
        }
        const _input = _history_offset > 0
            ? boxed_terminal_items.current[boxed_terminal_items.current.length - _history_offset].request
            : '';
        setInput({
            ...input,
            [device_id]: _input,
        });
        setHistoryOffset({
            ...history_offset,
            [device_id]: _history_offset,
        });
    }, [history_offset, getHistory, input, device_id]);
    const onInputKeyDown = useCallback((ev) => {
        switch (ev.key) {
            case 'Enter':
                ev.preventDefault();
                if (input[device_id] !== '') {
                    onSendCommand(input[device_id]);
                    setInput({
                        ...input,
                        [device_id]: '',
                    });
                    setHistoryOffset({
                        ...history_offset,
                        [device_id]: 0,
                    });
                }
                break;
            case 'ArrowUp':
                ev.preventDefault();
                adjustHistoryOffset(+1);
                break;
            case 'ArrowDown':
                ev.preventDefault();
                adjustHistoryOffset(-1);
                break;
        }
    }, [input, device_id, onSendCommand, history_offset, adjustHistoryOffset]);
    useEffect(() => {
        const el = terminal_ref.current;
        if (el != null) {
            if (['auto', 'scroll'].includes(getComputedStyle(el).overflowY)) {
                el.scrollTop = el.scrollHeight;
            }
        }
    }, [terminal_items]);
    return (_jsx(Paper, { sx: { mx: 2, mb: 1, height: '100%', maxHeight: 800 }, children: _jsxs(Box, { px: 2, py: 1, height: "100%", maxHeight: 800, children: [_jsxs(Box, { display: "flex", justifyContent: "space-between", children: [_jsx(Typography, { variant: "h6", children: "\u0422\u0435\u0440\u043C\u0438\u043D\u0430\u043B" }), is_staff && (_jsx(MaterialBoolControl, { label: "\u043F\u0435\u0440\u0435\u0445\u0432\u0430\u0442 \u0441\u043E\u043E\u0431\u0449\u0435\u043D\u0438\u0439", value: Boolean(debug_text_messages_CP ?? debug_text_message), onChange: onSetDebugTextMessages }))] }), _jsxs(Box, { className: "terminal", ref: terminal_ref, height: "100%", children: [terminal_items.map((command, i) => (_jsx(TerminalLine, { command: command }, i))), _jsx("form", { autoComplete: "off", children: _jsxs("div", { className: "terminal-input-line", children: [_jsx("span", { className: "terminal-prompt", children: ">\u00A0" }), _jsx("input", { type: "text", className: "terminal-input", name: "terminal-input", autoComplete: "off", value: input[device_id] ?? '', onChange: onInputChange, onKeyDown: onInputKeyDown })] }) })] }), show_hints && _jsx(TerminalHints, {})] }) }));
});
